import React, { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import type { ToolResultDetail } from '@/core/models/medconnect-agent.model'
import { normalizeUtf8 } from '@/utils/encoding'
import { useFinalizeDocumentMutation } from '@/core/services/medconnect-agent-service'

/** Document generation tools (see apiGateway medconnect-agent/tools/document). */
export const DRAFT_DOCUMENT_TOOL_NAME = 'draft_document'
export const FINALIZE_DOCUMENT_TOOL_NAME = 'finalize_document'

export const isDocumentTool = (toolName?: string): boolean =>
    toolName === DRAFT_DOCUMENT_TOOL_NAME || toolName === FINALIZE_DOCUMENT_TOOL_NAME

// ─── Parsing ──────────────────────────────────────────────────────────────────

function parseJsonResult(result: unknown): any {
    if (result === undefined || result === null) return undefined
    if (typeof result !== 'string') return result
    try {
        return JSON.parse(normalizeUtf8(result))
    } catch {
        return undefined
    }
}

export interface DraftDocumentResult {
    pending: boolean
    refused: boolean
    reason?: string
    draft?: string
}

/** `draft_document` result = `{ success, draft, nextStep }` or `{ success: false, refused: true, reason }`. */
export function parseDraftDocumentResult(toolInvocation: ToolResultDetail): DraftDocumentResult {
    if (toolInvocation.result === undefined) return { pending: true, refused: false }
    const parsed = parseJsonResult(toolInvocation.result)
    if (!parsed || typeof parsed !== 'object') {
        // Fall back to the raw text as the draft body rather than dropping the content.
        const text = typeof toolInvocation.result === 'string' ? normalizeUtf8(toolInvocation.result) : JSON.stringify(toolInvocation.result)
        return { pending: false, refused: false, draft: text }
    }
    if (parsed.refused) {
        return { pending: false, refused: true, reason: typeof parsed.reason === 'string' ? parsed.reason : '' }
    }
    return { pending: false, refused: false, draft: typeof parsed.draft === 'string' ? parsed.draft : '' }
}

export interface FinalizeDocumentToolResult {
    pending: boolean
    filename?: string
    link?: string
    message?: string
}

/** `finalize_document` result = `{ success, filename, key, attachedTo, message, _links }`. */
export function parseFinalizeDocumentResult(toolInvocation: ToolResultDetail): FinalizeDocumentToolResult {
    if (toolInvocation.result === undefined) return { pending: true }
    const parsed = parseJsonResult(toolInvocation.result)
    const links: string[] =
        parsed?._links && typeof parsed._links === 'object'
            ? Object.values(parsed._links).filter((v): v is string => typeof v === 'string')
            : []
    return {
        pending: false,
        filename: typeof parsed?.filename === 'string' ? parsed.filename : undefined,
        link: links[0],
        message: typeof parsed?.message === 'string' ? parsed.message : undefined,
    }
}

/** Prefill title from the `documentType` tool-call arg (e.g. "certificat de présence" → "Certificat de présence"). */
export function deriveDraftTitle(toolArgs?: Record<string, any>): string {
    const documentType = typeof toolArgs?.documentType === 'string' ? toolArgs.documentType.trim() : ''
    if (!documentType) return ''
    return documentType.charAt(0).toUpperCase() + documentType.slice(1)
}

// ─── Shared frame ─────────────────────────────────────────────────────────────

const CardFrame: React.FC<{ compact?: boolean; error?: boolean; header: React.ReactNode; children?: React.ReactNode }> = ({ compact = false, error = false, header, children }) => (
    <Box
        sx={{
            my: 0.75,
            p: compact ? 1 : 1.5,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: error ? 'warning.light' : 'divider',
            backgroundColor: 'action.hover',
        }}
    >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: children ? 0.75 : 0, minWidth: 0 }}>
            {header}
        </Stack>
        {children}
    </Box>
)

// ─── draft_document ───────────────────────────────────────────────────────────

export interface DocumentDraftCardProps {
    toolInvocation: ToolResultDetail
    compact?: boolean
    /** When the chat exposes a way to focus its input, the "ask a change" button appears. */
    onAskChange?: () => void
}

/**
 * Editable draft of a professional document: the practitioner reviews/edits the title
 * and body, then generates the letterhead PDF directly (POST /medconnect-agent/documents/finalize).
 */
export const DocumentDraftCard: React.FC<DocumentDraftCardProps> = ({ toolInvocation, compact = false, onAskChange }) => {
    const { t, i18n } = useTranslation()
    const parsed = useMemo(() => parseDraftDocumentResult(toolInvocation), [toolInvocation])
    const derivedTitle = useMemo(() => deriveDraftTitle(toolInvocation.tool_args), [toolInvocation])
    const [editedTitle, setEditedTitle] = useState<string | null>(null)
    const [editedBody, setEditedBody] = useState<string | null>(null)
    const [finalizeDocument, { isLoading, isSuccess, data, error }] = useFinalizeDocumentMutation()

    const title = editedTitle ?? derivedTitle
    const body = editedBody ?? parsed.draft ?? ''

    const patientId = typeof toolInvocation.tool_args?.patientId === 'string' ? toolInvocation.tool_args.patientId : undefined
    const consultationId = typeof toolInvocation.tool_args?.consultationId === 'string' ? toolInvocation.tool_args.consultationId : undefined
    // Finalization follows the live application selection, not a stale/model-authored tool argument.
    const language = i18n.language || (typeof toolInvocation.tool_args?.language === 'string' ? toolInvocation.tool_args.language : undefined)
    const recordLink = consultationId ? `/consultations/${consultationId}` : patientId ? `/patients/${patientId}` : undefined

    const handleGenerate = () => {
        if (!title.trim() || !body.trim() || isLoading) return
        void finalizeDocument({ title: title.trim(), content: body, patientId, consultationId, language })
    }

    const header = (
        <>
            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {parsed.refused ? t('page.medconnectAgent.documentDraft.refusedTitle') : t('page.medconnectAgent.documentDraft.cardTitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {parsed.pending && <CircularProgress size={12} color="inherit" />}
            {parsed.refused && <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
            {isSuccess && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        </>
    )

    // ── Pending: the tool call is still running ──
    if (parsed.pending) {
        return (
            <CardFrame compact={compact} header={header}>
                {typeof toolInvocation.tool_args?.documentType === 'string' && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {toolInvocation.tool_args.documentType}
                    </Typography>
                )}
            </CardFrame>
        )
    }

    // ── Refused (regulated document) ──
    if (parsed.refused) {
        return (
            <CardFrame compact={compact} error header={header}>
                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                    {parsed.reason}
                </Typography>
            </CardFrame>
        )
    }

    // ── Generated: success state ──
    if (isSuccess && data) {
        return (
            <CardFrame compact={compact} header={header}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                        {data.filename}
                    </Typography>
                    <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />} label={t('page.medconnectAgent.documentDraft.generated')} />
                    {recordLink && (
                        <Link component={RouterLink} to={recordLink} underline="hover" variant="body2">
                            {t('page.medconnectAgent.documentDraft.openRecord')}
                        </Link>
                    )}
                </Stack>
            </CardFrame>
        )
    }

    // ── Editable draft ──
    return (
        <CardFrame compact={compact} header={header}>
            <Stack spacing={1.25}>
                <TextField
                    size="small"
                    fullWidth
                    value={title}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder={t('page.medconnectAgent.documentDraft.titlePlaceholder')}
                    inputProps={{ 'aria-label': t('page.medconnectAgent.documentDraft.titlePlaceholder') }}
                />
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={compact ? 4 : 6}
                    maxRows={24}
                    value={body}
                    onChange={(e) => setEditedBody(e.target.value)}
                    label={t('page.medconnectAgent.documentDraft.bodyLabel')}
                    sx={{ '& textarea': { fontSize: compact ? '0.84rem' : '0.9rem', lineHeight: 1.5 } }}
                />
                {error !== undefined && (
                    <Typography variant="body2" sx={{ color: 'error.main' }}>
                        {t('page.medconnectAgent.documentDraft.error')}
                        {typeof (error as any)?.data?.message === 'string' ? ` — ${(error as any).data.message}` : ''}
                    </Typography>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {onAskChange && (
                        <Button size="small" color="inherit" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={onAskChange}>
                            {t('page.medconnectAgent.documentDraft.askChange')}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        disabled={isLoading || !title.trim() || !body.trim()}
                        startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleGenerate}
                    >
                        {isLoading ? t('page.medconnectAgent.documentDraft.generating') : t('page.medconnectAgent.documentDraft.generate')}
                    </Button>
                </Stack>
            </Stack>
        </CardFrame>
    )
}

// ─── finalize_document ────────────────────────────────────────────────────────

/** Success card for the agent's own `finalize_document` tool: filename + link from `_links`. */
export const FinalizeDocumentDisplay: React.FC<{ toolInvocation: ToolResultDetail; compact?: boolean }> = ({ toolInvocation, compact = false }) => {
    const { t } = useTranslation()
    const parsed = useMemo(() => parseFinalizeDocumentResult(toolInvocation), [toolInvocation])

    const header = (
        <>
            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('page.medconnectAgent.documentDraft.generated')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {parsed.pending ? <CircularProgress size={12} color="inherit" /> : <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        </>
    )

    if (parsed.pending) return <CardFrame compact={compact} header={header} />

    return (
        <CardFrame compact={compact} header={header}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                {parsed.filename && (
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                        {parsed.filename}
                    </Typography>
                )}
                {parsed.link && (
                    <Link component={RouterLink} to={parsed.link} underline="hover" variant="body2">
                        {t('page.medconnectAgent.documentDraft.openRecord')}
                    </Link>
                )}
            </Stack>
        </CardFrame>
    )
}

/** Renders the right component for a document-generation tool; `null` for other tools. */
export const DocumentToolDisplay: React.FC<{ toolInvocation: ToolResultDetail; compact?: boolean; onAskChange?: () => void }> = ({ toolInvocation, compact, onAskChange }) => {
    if (toolInvocation.tool_name === DRAFT_DOCUMENT_TOOL_NAME) return <DocumentDraftCard toolInvocation={toolInvocation} compact={compact} onAskChange={onAskChange} />
    if (toolInvocation.tool_name === FINALIZE_DOCUMENT_TOOL_NAME) return <FinalizeDocumentDisplay toolInvocation={toolInvocation} compact={compact} />
    return null
}

export default DocumentDraftCard
